import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import axios from 'axios';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';

function NewTaskDialog({ open, onClose, accounts, org, me, accesstoken, setAccounts }) {
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState(dayjs().format('YYYY-MM-DDTHH:mm'));
    const [assignee, setAssignee] = useState(me?.email || '');
    const [accountId, setAccountId] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setDueDate(dayjs().format('YYYY-MM-DDTHH:mm'));
    }, [open]);

    const accountOptions = (accounts || []).map(a => ({ id: a.id, name: a.name })).filter(a => a.name);

    async function onSave() {
        if (!description.trim() || !accountId) return;
        setSaving(true);
        try {
            const account = accounts.find(a => a.id === accountId);
            if (!account) return;
            const newReminder = {
                id: uuidv4(),
                description: description.trim(),
                dueDate: new Date(dueDate).toISOString(),
                status: 'open',
                assignee: assignee || me?.email,
                createdBy: me?.email,
            };
            const updatedAccount = { ...account, reminders: [...(account.reminders || []), newReminder] };
            // Update local state
            if (setAccounts) setAccounts(prev => prev.map(a => a.id === accountId ? updatedAccount : a));
            // Persist
            const _account = { ...updatedAccount };
            _account.updates = _account.updates?.map(u => !u.pendingDelete ? u : { ..._.omit(u, 'pendingDelete'), deleted: true });
            _account.updates = _account.updates?.map(u => !u.editHtml ? u : { ..._.omit(u, 'editHtml', 'editTags'), html: u.editHtml, tags: u.editTags });
            _account.updates = _account.updates?.map(u => _.omit(u, 'wasEdited'));
            await axios.put(`/api/accounts/${account.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { account: _.omit(_account, ['created', 'modified', 'log']) });
            onClose();
            setDescription('');
            setAccountId(null);
            setAssignee(me?.email || '');
        } catch (e) {
            console.error('Failed to create task', e);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>New Task</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                <TextField
                    autoFocus
                    label='Task description'
                    fullWidth
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                />
                <Autocomplete
                    options={accountOptions}
                    getOptionLabel={o => o.name || ''}
                    value={accountOptions.find(a => a.id === accountId) || null}
                    onChange={(e, v) => setAccountId(v?.id || null)}
                    renderInput={(params) => <TextField {...params} label='Account' />}
                />
                <TextField
                    label='Due date & time'
                    type='datetime-local'
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={dueDate}
                    onChange={e => setDueDate(e.target.value)}
                />
                <Box>
                    <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 0.5 }}>Assign to</Typography>
                    <Select
                        fullWidth
                        size='small'
                        value={assignee}
                        onChange={e => setAssignee(e.target.value)}
                    >
                        {(org?.users || []).map(u => (
                            <MenuItem key={u.email} value={u.email}>{u.email.split('@')[0]}</MenuItem>
                        ))}
                    </Select>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='outlined' disabled={saving}>Cancel</Button>
                <Button onClick={onSave} variant='contained' disabled={saving || !description.trim() || !accountId}>
                    {saving ? 'Creating...' : 'Create Task'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default NewTaskDialog;
